import {
  Collection,
  define,
  Events,
  Model,
  TransactionOptions,
  type,
  attributes
} from "@type-r/models";

/**
 * Model base class which reads its attributes from the local storage,
 * and saves them back when they are modified.
 */
@define
export class LocalStorageModel extends Model {
  initialize() {
    this.fetchAttrs(true);
  }

  // Serialize given attribute, but prevent the whole model serialization.
  toJSON(options: { attribute?: string } & TransactionOptions = {}) {
    return (
      options.attribute &&
      (this as any)._attributes[options.attribute].toJSON.call(
        this,
        this[options.attribute]
      )
    );
  }

  fetchAttrs(subscribe = false) {
    // Map attribute names to the JSON values read from localStorage.
    const attrs = this.mapObject((_, attribute) => {
      // Update local storage on attribute change.
      subscribe &&
        this.listenTo(this, "change:" + attribute, () => {
          localStorage.setItem(
            attribute,
            JSON.stringify(this.toJSON({ attribute }))
          );
        });

      return getAsJSON(attribute);
    });

    // Silently update the model, so it won't trigger the change event listener.
    this.set(attrs, { silent: true, parse: true });
  }
}

function getAsJSON(attribute) {
  const asString = localStorage.getItem(attribute);

  try {
    return JSON.parse(asString);
  } catch (e) {
    return asString;
  }
}

export function nestedIsValid(x: Model | Collection) {
  return !x || x.isValid();
}

nestedIsValid.error = function(attr: string) {
  return this[attr].validationError;
};

export interface SortOptions {
  attr: string;
  asc: boolean;
}

export function matchString(value: string, substr: string) {
  return value && value.toLowerCase().indexOf(substr.toLowerCase()) >= 0;
}

export function renameProperty(json: any, from: string, to: string) {
  if (json[from] !== undefined) {
    json[to] = json[from];
    delete json[from];
  }
}

export interface UploadSupportModel {
  doUpload(file, commentText, uploadType, documentType);
}

/**
 * Attributes mixin for the common created/updated fields
 */
export const createdUpdatedAttrs = {
  createdBy: type(String).dontSave,
  createdOn: type(Date).dontSave,
  updatedBy: type(String).dontSave,
  updatedOn: type(Date).dontSave
};

let _appConfig = null;

export function isFeatureEnabled(name: string) {
  return !_appConfig ||
    !_appConfig.appConfig ||
    !_appConfig.appConfig.experimentalFeatures
    ? false
    : Boolean(_appConfig.appConfig.experimentalFeatures[name]);
}

Events.on("appConfig:loaded", appConfig => {
  _appConfig = appConfig;
});

export type ChangeTransaction = InstanceType<typeof ChangeTransaction>;
export const ChangeTransaction = attributes({
  oldValues: Object,
  deltaChange: Object
});

export const FixedNumber = (digitsAfterDecPoint = 2) => {
  return type(Number).set(x => roundNumber(x, digitsAfterDecPoint));
};

export const roundNumber = (x: number, digitsAfterDecPoint: number = 2) => {
  const pow = Math.pow(10, digitsAfterDecPoint);

  return Math.round(x * pow) / pow;
};

export const filterLengthThreshold = (x: string, l = 2) =>
  x.length > l ? x : "";

const floatingComparisonPrecision = 0.001;
export const floatsEqual = (a: number, b: number, precision = floatingComparisonPrecision) => Math.abs(a - b) <= precision;
export const floatsLessThanOrEqual = (a: number, b: number, precision = floatingComparisonPrecision) => a <= b || floatsEqual(a, b, precision);
export const floatsGreaterThanOrEqual = (a: number, b: number, precision = floatingComparisonPrecision) => a >= b || floatsEqual(a, b, precision);
